import React, { useState } from 'react';
import { Timer } from 'lucide-react';
import type { TurnTiming } from './types';

const s = (ms?: number | null) => (ms == null ? '–' : `${(ms / 1000).toFixed(1)}s`);

/**
 * Admin + debug-mode chip under a bot bubble: how long the turn took from
 * sending the message until the human was needed again (the terminal `final`
 * frame — the composer re-enables on it). The server's own clock and its
 * milestones (first ack / first readable content / first cards / LLM time)
 * are in the expandable detail, so a slow turn can be placed at a glance:
 * network vs. server, waiting for the first word vs. waiting for the cards.
 */
const TurnClockChip: React.FC<{ timing: TurnTiming }> = ({ timing }) => {
  const [open, setOpen] = useState(false);
  const sv = timing.server || null;
  const serverMs = sv?.terminal_final_ms ?? sv?.total_ms;
  const slow = (sv?.slowest_nodes || []).map(n => `${n.node} ${s(n.ms)}`).join(', ');
  const secs = timing.clientMs / 1000;
  const tone = secs < 15 ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : secs < 40 ? 'bg-amber-50 text-amber-700 border-amber-200'
      : 'bg-red-50 text-red-700 border-red-200';
  return (
    <div className="mt-1">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        title="Turn clock: send → the moment it was your move again (debug mode)"
        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-mono ${tone}`}
      >
        <Timer className="w-3 h-3" />
        <span>{s(timing.clientMs)} to your move</span>
        {serverMs != null && <span className="opacity-70">· server {s(serverMs)}</span>}
        {sv?.llm_calls != null && <span className="opacity-70">· llm {sv.llm_calls}× {s(sv.llm_ms)}</span>}
      </button>
      {open && sv && (
        <div className="mt-1 rounded-lg border border-gray-200 bg-gray-50 px-2 py-1 text-[11px] font-mono text-gray-700 space-y-0.5">
          <div>first ack {s(sv.first_ack_ms)} · first content {s(sv.first_content_ms)} · first cards {s(sv.first_cards_ms)} · final {s(sv.terminal_final_ms)}</div>
          {slow && <div>slowest: {slow}</div>}
          <div className="opacity-70">client {s(timing.clientMs)} (network + render) vs server {s(serverMs)}</div>
        </div>
      )}
    </div>
  );
};

export default TurnClockChip;
